import React from "react";
import { Card, Empty, Row, Col, Badge, Button, Tooltip, Typography, Spin, Tag, Divider, Space, Alert } from "antd";
import { 
  TrophyOutlined,
  StarOutlined,
  MedicineBoxOutlined,
  DollarOutlined,
  MessageOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  TagOutlined
} from "@ant-design/icons";
import { PDPMRankingAnalysis, MedicalCondition } from "../../types/codeIdentifier";
import { useCopyUtilities } from "../../hooks/useCopyUtilities";
import ICDCodePills from "./ICDCodePills";

const { Text, Title } = Typography;

interface PDPMRankingViewProps {
  pdpmRankingAnalysis: PDPMRankingAnalysis | null;
  loadingFinalEvaluation: boolean;
  finalEvaluation: MedicalCondition[];
}

const PDPMRankingView: React.FC<PDPMRankingViewProps> = ({
  pdpmRankingAnalysis,
  loadingFinalEvaluation,
  finalEvaluation
}) => {
  const { copyToClipboard } = useCopyUtilities([]);

  // Helper function to extract code string from Icd10Code object
  const extractCodeString = (codeData: any): string => {
    if (!codeData) return '';
    
    // If it's already a string, return it (fallback for old format)
    if (typeof codeData === 'string') {
      return codeData;
    }
    
    // If it's an Icd10Code object, return the code
    if (typeof codeData === 'object' && codeData.code) {
      return codeData.code;
    }
    
    // Fallback
    return '';
  };

  // Helper function to extract description from Icd10Code object
  const extractDescriptionFromCode = (codeData: any): string | null => {
    if (typeof codeData === 'object' && codeData !== null) {
      return codeData.short_description || codeData.description || null;
    }
    return null;
  };

  // Helper function to get full code data from Icd10Code object
  const getCodeData = (codeInput: any) => {
    if (!codeInput) {
      return {
        code: '',
        description: 'Description not available',
        clinicalCategory: undefined,
        ntaPoints: undefined,
        isSLP: undefined
      };
    }
    
    // If it's already an Icd10Code object, use it directly
    if (typeof codeInput === 'object' && codeInput.code) {
      return {
        code: codeInput.code,
        description: codeInput.short_description || 'Description not available',
        clinicalCategory: codeInput.clinical_category,
        ntaPoints: codeInput.nta_points,
        isSLP: codeInput.is_slp
      };
    }
    
    // Fallback for string codes - look up from finalEvaluation
    const code = extractCodeString(codeInput);
    if (!code || !Array.isArray(finalEvaluation)) {
      return {
        code,
        description: 'Description not available',
        clinicalCategory: undefined,
        ntaPoints: undefined,
        isSLP: undefined
      };
    }
    
    const condition = finalEvaluation.find(
      condition => condition?.best_icd10_codes?.some(c => c.code === code)
    );
    const codeObj = condition?.best_icd10_codes?.find(c => c.code === code);
    
    return {
      code,
      description: codeObj?.short_description || 'Description not available',
      clinicalCategory: codeObj?.clinical_category,
      ntaPoints: codeObj?.nta_points,
      isSLP: codeObj?.is_slp
    };
  };

  // Helper function to get code color based on type
  const getCodeColor = (code: string, description: string) => {
    if (code === "No Code") return '#999';
    if (description === "Non Billable") return '#ff4d4f';
    return '#1890ff';
  };

  // Helper function to format code with description
  const formatCodeWithDescription = (codeInput: any): { code: string; description: string } => {
    const code = extractCodeString(codeInput);
    
    // First check if the code object itself has a description
    const embeddedDescription = extractDescriptionFromCode(codeInput);
    if (embeddedDescription) {
      return {
        code,
        description: embeddedDescription
      };
    }
    
    // Otherwise, get it from the code data
    const codeData = getCodeData(codeInput);
    return {
      code: codeData.code,
      description: codeData.description
    };
  };

  if (loadingFinalEvaluation) {
    return (
      <div style={{ textAlign: "center", padding: "48px" }}>
        <Spin size="large" />
        <Text style={{ display: "block", marginTop: "16px" }}>Analyzing PDPM rankings...</Text>
      </div>
    );
  }

  if (!pdpmRankingAnalysis) {
    return (
      <div style={{ textAlign: "center", padding: "48px" }}>
        <Empty 
          description="No PDPM ranking analysis available" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Primary Diagnosis Recommendation */}
      {pdpmRankingAnalysis.primary_diagnosis_recommendation && (
        <Card 
          style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <TrophyOutlined style={{ color: '#faad14', fontSize: '24px' }} />
              <Title level={4} style={{ margin: 0 }}>Primary Diagnosis Recommendation</Title>
            </div>
          }
        >
          <div style={{ 
            background: 'linear-gradient(135deg, #fff8e1 0%, #fffef5 100%)', 
            padding: '24px', 
            borderRadius: '8px', 
            border: '2px solid #ffd591'
          }}>
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <div style={{ marginBottom: '20px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Primary ICD-10 Code
                  </Text>
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '16px', 
                    background: 'rgba(255, 255, 255, 0.8)', 
                    borderRadius: '8px',
                    border: '1px solid #ffd591'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      {(() => {
                        const codeString = extractCodeString(pdpmRankingAnalysis.primary_diagnosis_recommendation.code) || 'N/A';
                        const { description } = formatCodeWithDescription(pdpmRankingAnalysis.primary_diagnosis_recommendation.code);
                        const codeColor = getCodeColor(codeString, description);
                        
                        return (
                          <Tag 
                            color={codeString === "No Code" ? "default" : description === "Non Billable" ? "red" : "gold"}
                            style={{ 
                              fontSize: '18px', 
                              padding: '8px 16px', 
                              fontWeight: 'bold',
                              marginTop: '2px',
                              color: codeString === "No Code" ? '#999' : description === "Non Billable" ? '#fff' : undefined
                            }}
                          >
                            {codeString}
                          </Tag>
                        );
                      })()}
                      <div style={{ flex: 1 }}>
                        <Text strong style={{ fontSize: '16px', color: '#1f1f1f', display: 'block', marginBottom: '4px' }}>
                          {formatCodeWithDescription(pdpmRankingAnalysis.primary_diagnosis_recommendation.code).description}
                        </Text>
                        <div style={{ marginTop: '6px' }}>
                          <ICDCodePills 
                            clinicalCategory={getCodeData(pdpmRankingAnalysis.primary_diagnosis_recommendation.code).clinicalCategory}
                            ntaPoints={getCodeData(pdpmRankingAnalysis.primary_diagnosis_recommendation.code).ntaPoints}
                            isSLP={getCodeData(pdpmRankingAnalysis.primary_diagnosis_recommendation.code).isSLP}
                            size="small"
                          />
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <Tooltip title="Copy code only">
                          <Button
                            type="text"
                            size="small"
                            icon={<TagOutlined />}
                            onClick={() => {
                              const codeString = extractCodeString(pdpmRankingAnalysis.primary_diagnosis_recommendation?.code);
                              copyToClipboard(codeString, 'Code');
                            }}
                            style={{ color: '#faad14', fontSize: '12px' }}
                          />
                        </Tooltip>
                        <Tooltip title="Copy code and description">
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => {
                              const codeString = extractCodeString(pdpmRankingAnalysis.primary_diagnosis_recommendation?.code);
                              const { description } = formatCodeWithDescription(codeString);
                              copyToClipboard(`${codeString} - ${description}`, 'Code and description');
                            }}
                            style={{ color: '#faad14' }}
                          />
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col span={24}>
                <div style={{ 
                  background: 'rgba(255, 255, 255, 0.8)', 
                  padding: '16px', 
                  borderRadius: '6px',
                  marginTop: '8px'
                }}>
                  <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase' }}>Justification</Text>
                  <Text style={{ display: 'block', marginTop: '8px', lineHeight: '1.6' }}>
                    {pdpmRankingAnalysis.primary_diagnosis_recommendation.justification || 'No justification provided'}
                  </Text>
                </div>
              </Col>
            </Row>
          </div>
        </Card>
      )}

      {/* Primary Diagnosis Alternatives */}
      {Array.isArray(pdpmRankingAnalysis.primary_diagnosis_alternatives) && pdpmRankingAnalysis.primary_diagnosis_alternatives.length > 0 && (
        <Card 
          style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <StarOutlined style={{ color: '#1890ff', fontSize: '24px' }} />
              <Title level={4} style={{ margin: 0 }}>Primary Diagnosis Alternatives</Title>
              <Tag color="blue">{pdpmRankingAnalysis.primary_diagnosis_alternatives.length} options</Tag>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {pdpmRankingAnalysis.primary_diagnosis_alternatives
              .sort((a, b) => (a?.rank || 0) - (b?.rank || 0))
              .map((item, index) => {
                if (!item) return null;
                const { code, description } = formatCodeWithDescription(item.code);
                return (
              <Card 
                key={index}
                size="small"
                style={{
                  borderRadius: '8px',
                  background: '#f0f5ff',
                  border: '1px solid #91d5ff'
                }}
              >
                <Row gutter={[16, 8]}>
                  <Col xs={24} sm={8}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Badge 
                          count={`#${item.rank || index + 1}`} 
                          style={{ 
                            backgroundColor: '#1890ff',
                            fontWeight: 'bold'
                          }} 
                        />
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                          <Tag 
                            color={code === "No Code" ? "default" : description === "Non Billable" ? "red" : "blue"} 
                            style={{ fontSize: '14px' }}
                          >
                            {code}
                          </Tag>
                          {code !== "No Code" && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <Tooltip title="Copy code only">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<TagOutlined />}
                                  onClick={() => copyToClipboard(code, 'Code')}
                                  style={{ color: getCodeColor(code, description), fontSize: '12px' }}
                                />
                              </Tooltip>
                              <Tooltip title="Copy code and description">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(`${code} - ${description}`, 'Code and description')}
                                  style={{ color: getCodeColor(code, description) }}
                                />
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      </div>
                      <Text style={{ 
                        fontSize: '12px', 
                        color: description === "Non Billable" ? '#ff4d4f' : '#666', 
                        fontWeight: '500', 
                        lineHeight: '1.4' 
                      }}>
                        {description}
                      </Text>
                      <ICDCodePills 
                        clinicalCategory={getCodeData(item.code).clinicalCategory}
                        ntaPoints={getCodeData(item.code).ntaPoints}
                        isSLP={getCodeData(item.code).isSLP}
                        size="small"
                      />
                    </div>
                  </Col>
                  <Col xs={24} sm={16}>
                    <div>
                      <Text style={{ fontSize: '13px', color: '#595959', lineHeight: '1.5' }}>
                        {item.justification || 'No justification provided'}
                      </Text>
                    </div>
                  </Col>
                </Row>
              </Card>
                );
            })}
          </div>
        </Card>
      )}

      {/* Secondary Diagnoses */}
      {Array.isArray(pdpmRankingAnalysis.non_primary_diagnoses) && pdpmRankingAnalysis.non_primary_diagnoses.length > 0 && (
        <Card 
          style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <DollarOutlined style={{ color: '#52c41a', fontSize: '24px' }} />
              <Title level={4} style={{ margin: 0 }}>Secondary Diagnoses</Title>
              <Tag color="green">{pdpmRankingAnalysis.non_primary_diagnoses.length} codes</Tag>
            </div>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {pdpmRankingAnalysis.non_primary_diagnoses
              .filter(item => item != null) // Filter out null/undefined items
              .sort((a, b) => {
                // Define impact priority order
                const getImpactPriority = (impact: string | undefined) => {
                  if (!impact) return 0;
                  const impactLower = impact.toLowerCase();
                  if (impactLower.includes('significant') || impactLower.includes('high')) return 3;
                  if (impactLower.includes('moderate') || impactLower.includes('medium')) return 2;
                  if (impactLower.includes('low') || impactLower.includes('minimal')) return 1;
                  return 0; // Unknown/other
                };
                
                const aPriority = Math.max(
                  getImpactPriority(a?.complexity_impact),
                  getImpactPriority(a?.reimbursement_impact)
                );
                const bPriority = Math.max(
                  getImpactPriority(b?.complexity_impact),
                  getImpactPriority(b?.reimbursement_impact)
                );
                
                return bPriority - aPriority; // Sort high to low
              })
              .map((item, index) => {
                if (!item) return null;
                const { code, description } = formatCodeWithDescription(item.code);
                
                // Determine color theme based on impact level
                const getImpactLevel = (complexity: string | undefined, reimbursement: string | undefined) => {
                  const checkImpact = (text: string | undefined) => {
                    if (!text) return 'unknown';
                    const lower = text.toLowerCase();
                    if (lower.includes('significant') || lower.includes('high')) return 'high';
                    if (lower.includes('moderate') || lower.includes('medium')) return 'moderate';
                    if (lower.includes('low') || lower.includes('minimal')) return 'low';
                    return 'unknown';
                  };
                  
                  const complexityLevel = checkImpact(complexity);
                  const reimbursementLevel = checkImpact(reimbursement);
                  
                  // Return the highest impact level
                  if (complexityLevel === 'high' || reimbursementLevel === 'high') return 'high';
                  if (complexityLevel === 'moderate' || reimbursementLevel === 'moderate') return 'moderate';
                  if (complexityLevel === 'low' || reimbursementLevel === 'low') return 'low';
                  return 'unknown';
                };
                
                const impactLevel = getImpactLevel(item.complexity_impact, item.reimbursement_impact);
                
                const getColorTheme = (level: string, shouldDocument: boolean) => {
                  if (level === 'high') {
                    return {
                      background: '#fff2e8',
                      border: '#ffbb96',
                      tagColor: 'red',
                      checkColor: '#ff4d4f'
                    };
                  } else if (level === 'moderate') {
                    return {
                      background: '#f6ffed',
                      border: '#b7eb8f',
                      tagColor: 'orange',
                      checkColor: '#fa8c16'
                    };
                  } else if (level === 'low') {
                    return {
                      background: '#e6f7ff',
                      border: '#91d5ff',
                      tagColor: 'blue',
                      checkColor: '#1890ff'
                    };
                  } else {
                    return {
                      background: '#fafafa',
                      border: '#d9d9d9',
                      tagColor: 'default',
                      checkColor: '#8c8c8c'
                    };
                  }
                };
                
                const colorTheme = getColorTheme(impactLevel, item.should_document || false);
                
                return (
                  <div
                    key={index}
                    style={{
                      padding: '16px',
                      paddingTop: '40px', // Extra padding to make room for the impact tag
                      borderRadius: '8px',
                      background: colorTheme.background,
                      border: `1px solid ${colorTheme.border}`,
                      position: 'relative'
                    }}
                  >
                    {/* Impact level indicator */}
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      zIndex: 1
                    }}>
                      <Tag 
                        color={impactLevel === 'high' ? 'red' : impactLevel === 'moderate' ? 'orange' : impactLevel === 'low' ? 'blue' : 'default'}
                        style={{ 
                          margin: 0, 
                          fontSize: '10px', 
                          textTransform: 'uppercase',
                          lineHeight: '1.2',
                          padding: '2px 6px'
                        }}
                      >
                        {impactLevel} Impact
                      </Tag>
                    </div>
                    
                    <Row gutter={[16, 12]} align="top">
                      <Col xs={24} sm={8}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Tag 
                              color={code === "No Code" ? "default" : description === "Non Billable" ? "red" : colorTheme.tagColor} 
                              style={{ fontSize: '14px' }}
                            >
                              {code}
                            </Tag>
                            {code !== "No Code" && (
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <Tooltip title="Copy code only">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<TagOutlined />}
                                    onClick={() => copyToClipboard(code, 'Code')}
                                    style={{ 
                                      color: code === "No Code" ? '#999' : description === "Non Billable" ? '#ff4d4f' : 
                                        colorTheme.tagColor === 'red' ? '#ff4d4f' : colorTheme.tagColor === 'orange' ? '#fa8c16' : '#1890ff', 
                                      fontSize: '12px' 
                                    }}
                                  />
                                </Tooltip>
                                <Tooltip title="Copy code and description">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() => copyToClipboard(`${code} - ${description}`, 'Code and description')}
                                    style={{ 
                                      color: code === "No Code" ? '#999' : description === "Non Billable" ? '#ff4d4f' : 
                                        colorTheme.tagColor === 'red' ? '#ff4d4f' : colorTheme.tagColor === 'orange' ? '#fa8c16' : '#1890ff' 
                                    }}
                                  />
                                </Tooltip>
                              </div>
                            )}
                            {item.should_document && (
                              <CheckCircleOutlined style={{ color: colorTheme.checkColor }} />
                            )}
                          </div>
                          <Text style={{ 
                            fontSize: '12px', 
                            color: description === "Non Billable" ? '#ff4d4f' : '#666', 
                            fontWeight: '500', 
                            lineHeight: '1.4' 
                          }}>
                            {description}
                          </Text>
                          <div style={{ marginTop: '6px' }}>
                            <ICDCodePills 
                              clinicalCategory={getCodeData(item.code).clinicalCategory}
                              ntaPoints={getCodeData(item.code).ntaPoints}
                              isSLP={getCodeData(item.code).isSLP}
                              size="small"
                            />
                          </div>
                        </div>
                      </Col>
                      <Col xs={24} sm={16}>
                        <Text strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
                          {item.description || 'No description available'}
                        </Text>
                        <Space size="large" wrap>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            <InfoCircleOutlined /> Complexity: {item.complexity_impact || 'Unknown'}
                          </Text>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            <DollarOutlined /> Reimbursement: {item.reimbursement_impact || 'Unknown'}
                          </Text>
                        </Space>
                      </Col>
                    </Row>
                  </div>
                );
              })}
          </div>
        </Card>
      )}

      <Row gutter={[16, 16]}>
        {/* NTA Codes */}
        <Col xs={24} md={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MedicineBoxOutlined style={{ color: '#722ed1', fontSize: '24px' }} />
                <Title level={4} style={{ margin: 0 }}>NTA Qualifying Codes</Title>
              </div>
            }
            style={{ height: '100%', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}
          >
            {Array.isArray(pdpmRankingAnalysis.nta_codes) && pdpmRankingAnalysis.nta_codes.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {pdpmRankingAnalysis.nta_codes
                  .filter(item => item != null) // Filter out null/undefined items
                  .sort((a, b) => (b?.nta_points || 0) - (a?.nta_points || 0)) // Sort by NTA points (high to low)
                  .map((item, index) => {
                    if (!item) return null;
                    const { code, description } = formatCodeWithDescription(item.code);
                    
                    // Determine color theme based on NTA points
                    const getNTAColorTheme = (points: number) => {
                      if (points >= 3) {
                        return {
                          background: '#fff0f6',
                          border: '#ffadd2',
                          badgeColor: '#722ed1',
                          priority: 'high'
                        };
                      } else if (points >= 1) {
                        return {
                          background: '#f9f0ff',
                          border: '#d3adf7',
                          badgeColor: '#9254de',
                          priority: 'medium'
                        };
                      } else {
                        return {
                          background: '#f5f5f5',
                          border: '#d9d9d9',
                          badgeColor: '#8c8c8c',
                          priority: 'none'
                        };
                      }
                    };
                    
                    const colorTheme = getNTAColorTheme(item.nta_points || 0);
                    
                    return (
                      <div
                        key={index}
                        style={{
                          padding: '12px',
                          paddingTop: '32px',
                          borderRadius: '8px',
                          background: colorTheme.background,
                          border: `1px solid ${colorTheme.border}`,
                          position: 'relative'
                        }}
                      >
                        {/* Priority indicator */}
                        <div style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          zIndex: 1
                        }}>
                          <Tag 
                            color={colorTheme.priority === 'high' ? 'purple' : colorTheme.priority === 'medium' ? 'blue' : 'default'}
                            style={{ 
                              margin: 0, 
                              fontSize: '9px', 
                              textTransform: 'uppercase',
                              lineHeight: '1.2',
                              padding: '1px 4px'
                            }}
                          >
                            {colorTheme.priority === 'high' ? 'HIGH VALUE' : colorTheme.priority === 'medium' ? 'MEDIUM VALUE' : 'NO POINTS'}
                          </Tag>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Tag color="purple" style={{ margin: 0, fontSize: '14px' }}>{code}</Tag>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <Tooltip title="Copy code only">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<TagOutlined />}
                                    onClick={() => copyToClipboard(code, 'Code')}
                                    style={{ color: '#722ed1', fontSize: '12px' }}
                                  />
                                </Tooltip>
                                <Tooltip title="Copy code and description">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() => copyToClipboard(`${code} - ${description}`, 'Code and description')}
                                    style={{ color: '#722ed1' }}
                                  />
                                </Tooltip>
                              </div>
                            </div>
                            <Text style={{ fontSize: '12px', color: '#666', fontWeight: '500', lineHeight: '1.4' }}>
                              {description}
                            </Text>
                          </div>
                          <Badge 
                            count={`${formatNumber(item.nta_points || 0)} NTA Points`} 
                            style={{ backgroundColor: colorTheme.badgeColor }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            ) : (
              <Empty description="No NTA qualifying codes" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>

        {/* SLP Codes */}
        <Col xs={24} md={12}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <MessageOutlined style={{ color: '#eb2f96', fontSize: '24px' }} />
                <Title level={4} style={{ margin: 0 }}>SLP Related Codes</Title>
              </div>
            }
            style={{ height: '100%', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}
          >
            {Array.isArray(pdpmRankingAnalysis.slp_codes) && pdpmRankingAnalysis.slp_codes.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {pdpmRankingAnalysis.slp_codes
                  .filter(item => item != null) // Filter out null/undefined items
                  .sort((a, b) => {
                    // Sort by SLP impact level
                    const getSLPPriority = (impact: string | undefined) => {
                      if (!impact) return 0;
                      const impactLower = impact.toLowerCase();
                      if (impactLower.includes('positive') && !impactLower.includes('potentially')) return 3;
                      if (impactLower.includes('potentially positive')) return 2;
                      if (impactLower.includes('neutral')) return 1;
                      return 0;
                    };
                    
                    return getSLPPriority(b?.impact) - getSLPPriority(a?.impact);
                  })
                  .map((item, index) => {
                    if (!item) return null;
                    const { code, description } = formatCodeWithDescription(item.code);
                    
                    // Determine color theme based on SLP impact
                    const getSLPColorTheme = (impact: string | undefined) => {
                      if (!impact) {
                        return {
                          background: '#f5f5f5',
                          border: '#d9d9d9',
                          priority: 'unknown',
                          tagColor: 'default'
                        };
                      }
                      const impactLower = impact.toLowerCase();
                      
                      if (impactLower.includes('positive') && !impactLower.includes('potentially')) {
                        return {
                          background: '#f6ffed',
                          border: '#b7eb8f',
                          priority: 'positive',
                          tagColor: 'green'
                        };
                      } else if (impactLower.includes('potentially positive')) {
                        return {
                          background: '#fff7e6',
                          border: '#ffd591',
                          priority: 'potential',
                          tagColor: 'orange'
                        };
                      } else if (impactLower.includes('neutral')) {
                        return {
                          background: '#e6f7ff',
                          border: '#91d5ff',
                          priority: 'neutral',
                          tagColor: 'blue'
                        };
                      } else {
                        return {
                          background: '#f5f5f5',
                          border: '#d9d9d9',
                          priority: 'unknown',
                          tagColor: 'default'
                        };
                      }
                    };
                    
                    const colorTheme = getSLPColorTheme(item.impact);
                    
                    return (
                      <div
                        key={index}
                        style={{
                          padding: '12px',
                          paddingTop: '32px',
                          borderRadius: '8px',
                          background: colorTheme.background,
                          border: `1px solid ${colorTheme.border}`,
                          position: 'relative'
                        }}
                      >
                        {/* Priority indicator */}
                        <div style={{
                          position: 'absolute',
                          top: '6px',
                          right: '6px',
                          zIndex: 1
                        }}>
                          <Tag 
                            color={colorTheme.tagColor}
                            style={{ 
                              margin: 0, 
                              fontSize: '9px', 
                              textTransform: 'uppercase',
                              lineHeight: '1.2',
                              padding: '1px 4px'
                            }}
                          >
                            {colorTheme.priority === 'positive' ? 'POSITIVE' : 
                             colorTheme.priority === 'potential' ? 'POTENTIAL' : 
                             colorTheme.priority === 'neutral' ? 'NEUTRAL' : 'UNKNOWN'}
                          </Tag>
                        </div>
                        
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <Tag color="magenta" style={{ margin: 0, fontSize: '14px' }}>{code}</Tag>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <Tooltip title="Copy code only">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<TagOutlined />}
                                    onClick={() => copyToClipboard(code, 'Code')}
                                    style={{ color: '#eb2f96', fontSize: '12px' }}
                                  />
                                </Tooltip>
                                <Tooltip title="Copy code and description">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<CopyOutlined />}
                                    onClick={() => copyToClipboard(`${code} - ${description}`, 'Code and description')}
                                    style={{ color: '#eb2f96' }}
                                  />
                                </Tooltip>
                              </div>
                            </div>
                            <Text style={{ fontSize: '12px', color: '#666', fontWeight: '500', lineHeight: '1.4', display: 'block' }}>
                              {description}
                            </Text>
                            <div style={{ marginTop: '6px' }}>
                              <ICDCodePills 
                                clinicalCategory={getCodeData(item.code).clinicalCategory}
                                ntaPoints={getCodeData(item.code).ntaPoints}
                                isSLP={getCodeData(item.code).isSLP}
                                size="small"
                              />
                            </div>
                          </div>
                          {(() => {
                            // Clean up SLP relevance to remove technical data
                            const cleanSLPRelevance = (relevance: string | undefined): string => {
                              if (!relevance) return 'No relevance info';
                              
                              // If relevance contains technical data, try to extract clean parts
                              if (relevance.includes('Database lookup:') || 
                                  relevance.includes('vector_search_score') || 
                                  relevance.includes('short_description=')) {
                                
                                // Try to extract a clean relevance description
                                const relevanceMatch = relevance.match(/slp_relevance="([^"]+)"/);
                                if (relevanceMatch) {
                                  return relevanceMatch[1];
                                }
                                
                                // Fallback to a clean message
                                return 'SLP-related condition identified';
                              }
                              
                              return relevance;
                            };
                            
                            const cleanedRelevance = cleanSLPRelevance(item.slp_relevance);
                            
                            return (
                              <Text strong style={{ fontSize: '14px', color: '#eb2f96' }}>
                                {cleanedRelevance}
                              </Text>
                            );
                          })()}
                        </div>
                        {(() => {
                          // Clean up impact text to remove technical data
                          const cleanImpact = (impact: string | undefined): string => {
                            if (!impact) return 'Unknown';
                            
                            // If impact contains technical data, try to extract clean parts
                            if (impact.includes('Database lookup:') || 
                                impact.includes('vector_search_score') || 
                                impact.includes('short_description=')) {
                              
                              // Try to extract a clean impact description
                              const impactMatch = impact.match(/impact="([^"]+)"/);
                              if (impactMatch) {
                                return impactMatch[1];
                              }
                              
                              // Fallback to a clean message
                              return 'Identified through database lookup';
                            }
                            
                            return impact;
                          };
                          
                          const cleanedImpact = cleanImpact(item.impact);
                          
                          return (
                            <div style={{ 
                              padding: '8px 12px', 
                              backgroundColor: 'rgba(235, 47, 150, 0.1)', 
                              borderRadius: '6px',
                              border: '1px solid rgba(235, 47, 150, 0.2)'
                            }}>
                              <Text style={{ fontSize: '12px', color: '#eb2f96', fontWeight: '500' }}>
                                Impact: {cleanedImpact}
                              </Text>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
              </div>
            ) : (
              <Empty description="No SLP related codes" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </Card>
        </Col>
      </Row>

      {/* Summary Alert */}
      <Alert
        message="PDPM Analysis Complete"
        description={`Analyzed ${(pdpmRankingAnalysis.primary_diagnosis_alternatives || []).length + 1} potential primary diagnoses, ${(pdpmRankingAnalysis.non_primary_diagnoses || []).length} secondary diagnoses, ${(pdpmRankingAnalysis.nta_codes || []).length} NTA codes, and ${(pdpmRankingAnalysis.slp_codes || []).length} SLP codes.`}
        type="info"
        showIcon
        style={{ marginTop: '24px', borderRadius: '8px' }}
      />
    </div>
  );
};

export default PDPMRankingView; 